
import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { UserPlus } from "lucide-react";
import DomainElements from "@/components/gsa/DomainElements";
import { Domain } from "@/types/domain";
import KnowledgeNetworkStats from "@/components/gsa/KnowledgeNetworkStats";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

// NOAA domain elements
const domains: Domain[] = [{
  symbol: "Ocean Science",
  name: "Ocean Science",
  categories: "Marine Ecosystems, Oceanographic Data Collection, Ocean Acidification Research, Deep Sea Exploration, Ocean Modeling, Coral Reef Conservation"
}, {
  symbol: "Climate Science",
  name: "Climate Science",
  categories: "Climate Monitoring Systems, Greenhouse Gas Tracking, Climate Model Development, Historical Climate Analysis, Climate Adaptation Strategies, Climate Data Management"
}, {
  symbol: "Weather Services",
  name: "Weather Services",
  categories: "Weather Forecasting Methodology, Radar Technology, Storm Prediction, Severe Weather Warning Systems, Meteorological Data Analysis, Emergency Weather Communications"
}, {
  symbol: "Fisheries Management",
  name: "Fisheries Management",
  categories: "Sustainable Fisheries Practices, Marine Mammal Protection, Fishery Stock Assessment, International Fishery Agreements, Aquaculture Development, Habitat Conservation"
}, {
  symbol: "Satellite Systems",
  name: "Satellite Systems",
  categories: "Earth Observation Satellites, Remote Sensing Technology, Satellite Data Processing, Ground System Operations, Satellite Calibration, Data Visualization from Space"
}, {
  symbol: "Coastal Management",
  name: "Coastal Management",
  categories: "Coastal Zone Planning, Shoreline Change Analysis, Coastal Resilience Engineering, Coastal Habitat Restoration, Coastal Economics, Coastal Community Engagement"
}, {
  symbol: "Hazard Assessment",
  name: "Hazard Assessment",
  categories: "Tsunami Warning Systems, Hurricane Tracking, Flood Prediction Models, Drought Monitoring, Wildfire Weather Support, Volcanic Ash Monitoring"
}, {
  symbol: "Aviation Weather",
  name: "Aviation Weather",
  categories: "Aviation Forecasting Systems, Aircraft Icing Detection, Turbulence Prediction, Visibility Assessment, Terminal Aerodrome Forecasts, Air Traffic Management Weather Support"
}];
const networkStats = {
  totalParticipants: 834,
  activeContributors: 376,
  knowledgeBases: 692,
  totalSolutions: 247,
  departments: [{
    name: "National Weather Service",
    participants: 245
  }, {
    name: "National Ocean Service",
    participants: 187
  }, {
    name: "National Marine Fisheries Service",
    participants: 156
  }, {
    name: "NESDIS Satellite and Information Service",
    participants: 129
  }, {
    name: "Other NOAA Offices",
    participants: 117
  }]
};

const NoaaChannel = () => {
  return <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-6">
              <img src="/lovable-uploads/96fa6c65-feaa-4c8e-a94f-eaa594519caf.png" alt="US Federal Government Giblet" className="w-28 h-28 object-contain" />
              <div className="flex-grow">
                <CardTitle className="text-2xl font-bold mb-2">US Agency Channel</CardTitle>
                <CardDescription className="text-lg">
                  The place where former federal employees impacted by Department of Government Efficiency (DOGE) cuts continue to serve the American people
                </CardDescription>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3">
                    <Link to="/gsa-channel">
                      <img src="/lovable-uploads/3e0cc269-0dd5-40b5-a1f0-0ebbe7e85733.png" alt="GSA Giblet" className="w-12 h-12 object-contain hover:scale-110 transition-transform cursor-pointer" title="General Services Administration" />
                    </Link>
                    <Link to="/dept-education-channel">
                      <img src="/lovable-uploads/79ab86f8-bc15-4c23-aa17-3b68e2637276.png" alt="DoE Giblet" className="w-12 h-12 object-contain hover:scale-110 transition-transform cursor-pointer" title="Department of Education" />
                    </Link>
                    <Link to="/noaa-channel">
                      <img src="/lovable-uploads/bab08742-8e14-4791-9d8d-dc0b1fd81a3a.png" alt="NOAA Giblet" className="w-12 h-12 object-contain hover:scale-110 transition-transform cursor-pointer" title="National Oceanic and Atmospheric Agency" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">NOAA Dashboard</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="col-span-2">
                  <KnowledgeNetworkStats stats={networkStats} />
                </div>
                <div className="flex flex-col gap-4">
                  <Card className="p-3 bg-blue-50">
                    <div className="flex items-center space-x-3 mb-2">
                      <Avatar className="h-12 w-12 border-2 border-blue-500">
                        <AvatarImage src="/lovable-uploads/416de5b1-44bd-4e50-ad98-16826f32f81b.png" alt="Jim Collins" className="object-cover" />
                        <AvatarFallback className="bg-blue-100 text-blue-800 text-xl">JC</AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="text-lg font-semibold">Network Ambassador</h3>
                        <Link to="/myspai-profile/jim-lawnin" className="text-primary hover:text-primary/80 font-medium">
                          Jim Collins
                        </Link>
                        <p className="text-sm text-gray-600">Former Chief Scientist, NOAA Climate Program</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">
                      Leading knowledge sharing and collaboration on climate and weather research across former NOAA specialists.
                    </p>
                    <Button variant="outline" size="sm" className="w-full">
                      <UserPlus className="mr-2 h-4 w-4" />
                      Join Knowledge Network
                    </Button>
                  </Card>
                </div>
              </div>
            </div>
            
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">NOAA Domain Elements</h2>
              <DomainElements domains={domains} />
            </div>
          </CardContent>
        </Card>
      </main>
    </div>;
};
export default NoaaChannel;
